// src/viewmodels/useStorageViewModel.ts
import { useState, useCallback } from 'react';

// Types
export interface Bucket {
  id: string;
  name: string;
  createdAt: string;
  size: number;
  fileCount: number;
  region: string;
}

export interface StorageFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  accessUrl: string;
  bucketId: string;
}

interface StorageState {
  buckets: Bucket[];
  selectedBucket: Bucket | null;
  files: StorageFile[];
  isLoading: boolean;
  error: string | null;
  uploadProgress: number | null;
}

// Mock API Service
const mockStorageApi = {
  listBuckets: async (): Promise<Bucket[]> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return [
      {
        id: '1',
        name: 'assets-bucket',
        createdAt: new Date().toISOString(),
        size: 1024 * 1024 * 100, // 100MB
        fileCount: 25,
        region: 'us-east-1'
      },
      {
        id: '2',
        name: 'media-bucket',
        createdAt: new Date().toISOString(),
        size: 1024 * 1024 * 250, // 250MB
        fileCount: 50,
        region: 'us-west-1'
      }
    ];
  },

  getBucketDetails: async (bucketId: string): Promise<Bucket> => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      id: bucketId,
      name: 'assets-bucket',
      createdAt: new Date().toISOString(),
      size: 1024 * 1024 * 100,
      fileCount: 25,
      region: 'us-east-1'
    };
  },

  listFiles: async (bucketId: string): Promise<StorageFile[]> => {
    await new Promise(resolve => setTimeout(resolve, 800));
    return Array.from({ length: 10 }, (_, index) => ({
      id: `file-${index}`,
      name: `file-${index}.png`,
      size: 1024 * 100, // 100KB
      type: 'image/png',
      uploadedAt: new Date().toISOString(),
      accessUrl: `https://example.com/files/${index}`,
      bucketId
    }));
  },

  createBucket: async (name: string, region: string): Promise<Bucket> => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    return {
      id: Math.random().toString(36).substr(2, 9),
      name,
      createdAt: new Date().toISOString(),
      size: 0,
      fileCount: 0,
      region
    };
  },

  deleteBucket: async (bucketId: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 1000));
  },

  uploadFile: async (
    bucketId: string,
    file: File,
    onProgress: (progress: number) => void
  ): Promise<StorageFile> => {
    // Simulate upload progress
    const totalChunks = 10;
    for (let i = 1; i <= totalChunks; i++) {
      await new Promise(resolve => setTimeout(resolve, 500));
      onProgress((i / totalChunks) * 100);
    }

    return {
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      accessUrl: `https://example.com/files/${file.name}`,
      bucketId
    };
  },

  deleteFile: async (bucketId: string, fileId: string): Promise<void> => {
    await new Promise(resolve => setTimeout(resolve, 800));
  }
};

export const useStorageViewModel = () => {
  const [state, setState] = useState<StorageState>({
    buckets: [],
    selectedBucket: null,
    files: [],
    isLoading: false,
    error: null,
    uploadProgress: null
  });

  // Fetch all buckets
  const fetchBuckets = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const buckets = await mockStorageApi.listBuckets();
      setState(prev => ({ ...prev, buckets, isLoading: false }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to fetch buckets',
        isLoading: false
      }));
    }
  }, []);

  // Fetch bucket details and its files
  const fetchBucketDetails = useCallback(async (bucketId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const [bucket, files] = await Promise.all([
        mockStorageApi.getBucketDetails(bucketId),
        mockStorageApi.listFiles(bucketId)
      ]);
      setState(prev => ({
        ...prev,
        selectedBucket: bucket,
        files,
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to fetch bucket details',
        isLoading: false
      }));
    }
  }, []);

  // Create new bucket
  const createBucket = useCallback(async (name: string, region: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const newBucket = await mockStorageApi.createBucket(name, region);
      setState(prev => ({
        ...prev,
        buckets: [...prev.buckets, newBucket],
        isLoading: false
      }));
      return newBucket;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to create bucket',
        isLoading: false
      }));
      throw error;
    }
  }, []);

  // Delete bucket
  const deleteBucket = useCallback(async (bucketId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      await mockStorageApi.deleteBucket(bucketId);
      setState(prev => ({
        ...prev,
        buckets: prev.buckets.filter(b => b.id !== bucketId),
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to delete bucket',
        isLoading: false
      }));
    }
  }, []);

  // Upload file
  const uploadFile = useCallback(async (bucketId: string, file: File) => {
    try {
      const onProgress = (progress: number) => {
        setState(prev => ({ ...prev, uploadProgress: progress }));
      };

      const newFile = await mockStorageApi.uploadFile(bucketId, file, onProgress);
      
      setState(prev => ({
        ...prev,
        files: [...prev.files, newFile],
        uploadProgress: null
      }));
      
      return newFile;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to upload file',
        uploadProgress: null
      }));
      throw error;
    }
  }, []);

  // Delete file
  const deleteFile = useCallback(async (bucketId: string, fileId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      await mockStorageApi.deleteFile(bucketId, fileId);
      setState(prev => ({
        ...prev,
        files: prev.files.filter(f => f.id !== fileId),
        isLoading: false
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: 'Failed to delete file',
        isLoading: false
      }));
    }
  }, []);

  // Calculate total storage used
  const getTotalStorageUsed = useCallback(() => {
    return state.buckets.reduce((total, bucket) => total + bucket.size, 0);
  }, [state.buckets]);

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // State
    buckets: state.buckets,
    selectedBucket: state.selectedBucket,
    files: state.files,
    isLoading: state.isLoading,
    error: state.error,
    uploadProgress: state.uploadProgress,

    // Actions
    fetchBuckets,
    fetchBucketDetails,
    createBucket,
    deleteBucket,
    uploadFile,
    deleteFile,
    getTotalStorageUsed,
    clearError
  };
};